import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, cn, IconButton } from '@c2fo/liquidity';
import { UploadLightIcon, DownloadIcon, FileLightIcon } from '@c2fo/liquidity/icons';
import Skeleton from '@/enterprise/components/Skeleton';
import useCompanyDocumentMetadata from '@/enterprise/data/useCompanyDocumentMetadata';
import useUploadCompanyDocument, { CompanyDocumentType } from '@/enterprise/data/useUploadCompanyDocument';
import apiClient from '@/lib/apiClient';
import { triggerBlobDownload } from '@/utils/downloadCsvFromPost';
import formatFileSize from '@/utils/formatFileSize';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

interface CompanyDocumentUploadProps {
  documentType: CompanyDocumentType;
  label: string;
  description?: string;
  documentUuid: string | null;
  disabled?: boolean;
  onUploaded?: (documentType: CompanyDocumentType) => void;
}

const CompanyDocumentUpload = ({
  documentType,
  label,
  description,
  documentUuid,
  disabled = false,
  onUploaded,
}: CompanyDocumentUploadProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadDocument = useUploadCompanyDocument(documentType);
  const [fileTooLarge, setFileTooLarge] = useState(false);
  // The file the user picked in the current session — already has a real name/size client-side,
  // so it's used in place of fetching metadata for a document uploaded in a prior session.
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const uploaded = documentUuid !== null;
  const metadata = useCompanyDocumentMetadata(selectedFile ? null : documentUuid);
  // The metadata query is only enabled once documentUuid is set and no file is selected client-side
  // (see above) — `metadata.isPending` alone would stay true forever while it's disabled.
  const isLoadingMetadata = !selectedFile && documentUuid !== null && metadata.isPending;

  const documentFileName = selectedFile ? selectedFile.name : metadata.data?.fileName;
  const documentFileSize = selectedFile
    ? formatFileSize(selectedFile.size)
    : metadata.data?.size != null
    ? formatFileSize(metadata.data.size)
    : undefined;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      // Reason: reject oversized files before hashing — sha256Base64 reads the whole file into
      // memory, so there's no point paying that cost for something the backend will reject anyway.
      setFileTooLarge(true);
      return;
    }

    setFileTooLarge(false);
    setSelectedFile(file);
    uploadDocument.mutate(file, { onSuccess: () => onUploaded?.(documentType) });
  };

  const handleDownload = async () => {
    // The file just picked this session is already in memory — no need to re-fetch it.
    if (selectedFile) {
      triggerBlobDownload(selectedFile, selectedFile.name);
      return;
    }

    if (!documentUuid) {
      return;
    }

    setIsDownloading(true);
    setDownloadError(false);

    try {
      const blob = await apiClient.get(`api/supplier-experience/companies/documents/${documentUuid}/content`).blob();
      triggerBlobDownload(blob, documentFileName ?? label);
    } catch {
      setDownloadError(true);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-3 rounded-xl bg-canvas p-4', { 'opacity-38': disabled })}>
      <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
        <div className="flex flex-1 flex-col gap-1">
          <span className="font-medium text-text-primary">{label}</span>
          {description && <span className="text-sm text-text-secondary">{description}</span>}
        </div>
        {uploaded ? (
          isLoadingMetadata ? (
            <div className="flex w-full flex-1 items-center gap-4 rounded-lg bg-white p-4 shadow-sm">
              <Skeleton height="h-10" shadow={false} className="w-10 shrink-0 rounded-lg" />
              <div className="flex flex-1 flex-col gap-2">
                <Skeleton height="h-4" shadow={false} className="w-1/3 rounded" />
                <Skeleton height="h-4" shadow={false} className="w-1/5 rounded" />
              </div>
              <Skeleton height="h-9" shadow={false} className="w-9 shrink-0 rounded-full" />
            </div>
          ) : (
            <div className="flex w-full flex-1 items-center justify-between gap-4 rounded-lg bg-white p-4 shadow-sm">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div className="flex shrink-0 items-center justify-center rounded-lg bg-deepGray-50 p-2">
                  <FileLightIcon className="size-6 fill-text-secondary" />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-text-primary">
                    {documentFileName ?? t('instantPay.onboarding.documents.metadataError')}
                  </span>
                  <div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-2">
                    {documentFileSize && <span className="text-text-secondary">{documentFileSize}</span>}
                    <Chip
                      label={t('instantPay.onboarding.documents.uploaded')}
                      variant="outlined"
                      isRound
                      size="xs"
                      type="primary"
                    />
                  </div>
                </div>
              </div>
              <IconButton
                icon={DownloadIcon}
                name={t('instantPay.onboarding.documents.downloadUploaded')}
                variant="ancillary"
                size="sm"
                loading={isDownloading}
                onClick={handleDownload}
                className="[&>svg]:size-5"
              />
            </div>
          )
        ) : (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFileChange}
              disabled={disabled}
            />
            <Button
              variant="primary"
              icon={<UploadLightIcon className="fill-white" />}
              loading={uploadDocument.isPending}
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              {t('instantPay.onboarding.documents.uploadCta')}
            </Button>
          </>
        )}
      </div>
      {fileTooLarge ? (
        <span role="alert" className="text-sm text-error-500">
          {t('instantPay.onboarding.documents.uploadTooLarge')}
        </span>
      ) : uploadDocument.isError ? (
        <span role="alert" className="text-sm text-error-500">
          {t('instantPay.onboarding.documents.uploadError')}
        </span>
      ) : (
        downloadError && (
          <span role="alert" className="text-sm text-error-500">
            {t('instantPay.onboarding.documents.downloadError')}
          </span>
        )
      )}
    </div>
  );
};

export default CompanyDocumentUpload;
